import { Link } from 'react-router-dom';
import { DocumentSection } from '../components/DocumentSection';
import { SUPPORT_EMAIL } from '../lib/support';
import { heading, lead, textLink } from '../lib/ui';

/**
 * プライバシーポリシー（#121、ADR-022）。
 *
 * **App Store Connect が URL を必須にしている**ので、アプリの公開より先に用意する。アプリの
 * AI 鑑定の同意の画面（`AiConsentCard`）からもここへリンクする。**Google の OAuth の同意画面を本番に
 * 出すときにも、Google がこのページを読みに来る**（Google のアカウントの情報の扱いを書いた節が要る。#295）。
 *
 * 2026-10-05 にユーザーの判断で草案の断り書きを外し、確定版にした（専門家の確認は経ていない。#295）。
 *
 * ## 書き換えるときに気をつけること
 *
 * - **アプリの同意の文面（`AiConsentCard`）と食い違わせない。** 送り先・学習に使わないこと・
 *   保存と削除は両方に書いてある
 * - AI の同意の文面を実質的に変えたら、`AI_CONSENT_VERSION` を上げて同意を取り直す
 * - 個人情報保護法第 28 条は、外国にある第三者について「国名・その国の制度・第三者の措置」を
 *   示すことを求めている。第三者を増やしたらこの節に足す
 * - 集める情報を増やしたら、App Store Connect の App Privacy（栄養ラベル）と
 *   `PrivacyInfo.xcprivacy` も合わせる（ズレると審査で差し戻される）
 * - **内容を変えたら `UPDATED_AT` を更新する**
 */

/** 最終更新日。**内容を変えたら必ず更新する。** */
const UPDATED_AT = '2026-10-05';

const COLLECTED = [
  {
    what: 'アカウントの識別子',
    detail:
      'アプリが初回の起動で作る、利用者に結びつかないランダムな ID と、最後に使った日時。メールアドレスや氏名は、引き継ぎを設定しない限り受け取りません。',
  },
  {
    what: '描いた絵',
    detail:
      'AI 鑑定に出した絵と、そこから作られた選手の画像。自分で描いたアバターの画像。どちらも AI に送る前に同意を得て、同意した日時を記録します（アプリの画面で説明します）。',
  },
  {
    what: '選手の名前と戦績',
    detail: '利用者が付けた選手の名前、能力値、対戦と大会の記録。',
  },
  {
    what: 'ニックネームとアバター',
    detail:
      '利用者が付けたニックネームと、選んだり描いたりしたアバター。ほんとうの名前や学校の名前を入れないよう、アプリの画面でお願いしています。',
  },
  {
    what: '通報とブロックの記録',
    detail: 'だれが、どの選手やニックネームを、どの理由で通報したか。だれがだれをブロックしたか。',
  },
  {
    what: '通知の宛先（通知をオンにした場合のみ）',
    detail:
      '端末に通知を届けるために Apple が発行する、端末ごとの識別子（デバイストークン）。通知を届けることにだけ使います。',
  },
  {
    what: 'ログインの情報（引き継ぎを設定した場合のみ）',
    detail:
      'メールアドレス、または Apple / Google のアカウントの情報。Clerk（下記）が預かり、当方のサーバーは Clerk の発行する識別子と、どの方法でログインしたかだけを保存します（下の「Apple・Google のアカウントの情報」）。',
  },
  {
    what: 'アクセスの記録',
    detail:
      'アプリやこのサイトがサーバーを呼んだときの、IP アドレス・日時・呼び出した機能など。不具合の調査と、短い時間に大量の呼び出しを受けないようにするために使います。選手の名前や絵は記録に残しません。',
  },
];

const PURPOSES = [
  'AI が絵の素質を鑑定し、選手を作るため',
  '選手・戦績を保存し、次に起動したときに続けられるようにするため',
  '機種変更やアプリの入れ直しのあとも同じ選手に戻れるようにするため（引き継ぎ）',
  'ともだちと対戦の相手に、ニックネームとアバターを見せるため（ランキングや大会には出しません）',
  'みんなが見ても大丈夫な絵かを確かめるため（選手の絵とアバター）',
  '通報を確かめ、利用規約に反する使い方に対応するため（選手を見えなくする・利用を止めるなど）',
  '大会の試合がもうすぐ始まることと、大会が終わったことを通知で知らせるため（通知をオンにした場合のみ。宣伝の通知は送りません）',
  '不正なアクセスを防ぎ、不具合を調べ、サービスを改善するため',
];

const SIGN_IN_DATA = [
  {
    what: '受け取る情報',
    detail:
      'Google でログインした場合は、Google のアカウントのメールアドレス・名前・プロフィール画像。Apple でログインした場合は、Apple のアカウントのメールアドレス（「メールを非公開」を選んだ場合は Apple が作る転送用のアドレス）と名前。ログインに要る基本の情報だけで、連絡先・カレンダー・ファイルなど、ほかの情報には触れません。',
  },
  {
    what: '使い道',
    detail:
      '引き継ぎのログインで、同じ人であることを確かめるためだけに使います。ほかの利用者に見せることはなく（ニックネームとは別です）、広告や AI の学習には使いません。',
  },
  {
    what: '保管',
    detail:
      'Clerk（下記）が保管します。当方のサーバーが保存するのは、Clerk の発行する識別子と、Apple・Google・メールのどれでログインしたかだけです。',
  },
  {
    what: '第三者への提供',
    detail:
      'Clerk のほかには渡しません。販売もしません。法令にもとづいて求められた場合を除きます。',
  },
  {
    what: '削除',
    detail:
      'アプリでアカウントを削除すると、Clerk が保管している情報も消します。Google や Apple のアカウントの設定から、drawgon とのつながりを外すこともできます。',
  },
];

const THIRD_PARTIES = [
  {
    name: 'Cloudflare, Inc.',
    role: 'サーバー・データベース・画像の保管・AI 鑑定とアバターの確認（Workers AI）',
    notes: [
      'データベースと画像は、アジア太平洋地域にある Cloudflare のデータセンターに保管しています。処理は世界各地にある Cloudflare のデータセンターで行われることがあります。',
      'AI 鑑定には Google が作った「Gemma」という AI を使いますが、Cloudflare の中で動かしており、絵が Google に送られることはありません。送った絵が AI の学習に使われることはありません（Cloudflare のデータ利用の方針）。',
      '当方と Cloudflare とのデータ処理の契約で、受け取った情報を当方の指示の範囲で扱い、安全管理の措置をとることが定められています。',
    ],
  },
  {
    name: 'Apple Inc.',
    role: '通知の配信（Apple Push Notification service）',
    notes: [
      '通知の文面（選手の名前を含みます）と、Apple が発行した端末の識別子だけを渡します。通知をオフにしている場合、何も渡りません。',
      'Apple のプライバシーポリシーに従って扱われます。',
    ],
  },
  {
    name: 'Clerk, Inc.',
    role: '引き継ぎのログイン（メール・Apple・Google）',
    notes: [
      '引き継ぎを設定しない場合、Clerk には何も渡りません。',
      '当方と Clerk とのデータ処理の契約で、受け取った情報を当方の指示の範囲で扱い、安全管理の措置をとることが定められています。',
    ],
  },
];

const SAFEGUARDS = [
  '情報を扱う担当者を限り、管理画面には管理者のアカウントでしか入れないようにしています',
  'アプリ・サイトとサーバーとの通信は暗号化しています',
  'サーバーの記録（ログ）に、選手の名前・絵の場所・通報した人を出さないようにしています',
  '情報を外国の事業者のサーバーで扱うため、その国の個人情報の保護に関する制度を把握したうえで、上記の措置をとっています',
];

export function Privacy() {
  return (
    <div className="space-y-6 pt-8">
      <header className="space-y-3">
        <h1 className={heading}>プライバシーポリシー</h1>
        <p className={lead}>
          drawgon（以下「本サービス」）が、利用者の情報をどのように扱うかを説明します。本サービスを使うときのきまりは
          <Link to="/terms" className={`${textLink} text-text`}>
            利用規約
          </Link>
          に書いています。
          <br />
          最終更新日: {UPDATED_AT}
        </p>
      </header>

      <DocumentSection title="だれが扱うか">
        <p className={lead}>
          weBaby合同会社（以下「当方」）が本サービスを提供し、情報を扱います。当方の住所と代表者の氏名は、
          お問い合わせいただければ、すぐにお答えします（問い合わせ先は本ページ末尾）。
        </p>
      </DocumentSection>

      <DocumentSection title="集める情報">
        <dl className="space-y-3">
          {COLLECTED.map((item) => (
            <div key={item.what}>
              <dt className="font-display text-sm">{item.what}</dt>
              <dd className="mt-0.5 text-sm text-text-muted">{item.detail}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 text-sm text-text-muted">
          位置情報・連絡先・端末の広告識別子は取得しません。広告は配信していません。
        </p>
      </DocumentSection>

      <DocumentSection title="何のために使うか">
        <ul className="space-y-1.5 text-sm text-text-muted">
          {PURPOSES.map((purpose) => (
            <li key={purpose}>・{purpose}</li>
          ))}
        </ul>
      </DocumentSection>

      <DocumentSection title="Apple・Google のアカウントの情報">
        <p className={lead}>
          引き継ぎを設定するときに Apple や Google
          のアカウントでログインした場合、その情報を次のように扱います。
        </p>
        <dl className="mt-4 space-y-3">
          {SIGN_IN_DATA.map((item) => (
            <div key={item.what}>
              <dt className="font-display text-sm">{item.what}</dt>
              <dd className="mt-0.5 text-sm text-text-muted">{item.detail}</dd>
            </div>
          ))}
        </dl>
      </DocumentSection>

      <DocumentSection title="外国にある第三者への提供">
        <p className={lead}>
          本サービスは次の事業者を利用しており、上記の情報はこれらの事業者のサーバーで扱われます。どの事業者もアメリカ合衆国の会社です
          （個人情報保護法第 28 条）。
        </p>
        <div className="mt-4 space-y-4">
          {THIRD_PARTIES.map((party) => (
            <div key={party.name}>
              <p className="font-display text-sm">{party.name}（アメリカ合衆国）</p>
              <p className="mt-0.5 text-sm text-text-muted">{party.role}</p>
              {party.notes.map((note) => (
                <p key={note} className="mt-0.5 text-sm text-text-muted">
                  {note}
                </p>
              ))}
            </div>
          ))}
        </div>
        <h3 className="mt-5 font-display text-sm">アメリカ合衆国の個人情報の保護に関する制度</h3>
        <p className="mt-1 text-sm text-text-muted">
          個人情報保護委員会の調査（2021 年 10
          月時点）によれば、アメリカ合衆国には個人情報の保護に関する包括的な連邦法はなく、
          通信・金融・医療など分野ごとの法律があります。事業者が所在するカリフォルニア州には、消費者のプライバシーに関する州法（CCPA）があります。
          アメリカ合衆国は APEC
          の越境プライバシールール（CBPR）システムに参加しており、事業者の義務と本人の権利について、一定の保護が期待できるとされています。
          詳しくは
          <a
            href="https://www.ppc.go.jp/personalinfo/legal/kaiseihogohou/#gaikoku"
            target="_blank"
            rel="noopener noreferrer"
            className={`${textLink} text-text`}
          >
            個人情報保護委員会の資料
          </a>
          をご覧ください。
        </p>
      </DocumentSection>

      <DocumentSection title="安全管理のための措置">
        <ul className="space-y-1.5 text-sm text-text-muted">
          {SAFEGUARDS.map((safeguard) => (
            <li key={safeguard}>・{safeguard}</li>
          ))}
        </ul>
      </DocumentSection>

      <DocumentSection title="保存する期間と、消し方">
        <p className={lead}>
          集めた情報は、アカウントを削除するまで保存します。アプリの「データ引き継ぎ」の画面から、いつでもアカウントを削除できます。
          削除すると、選手・戦績・描いた絵・ニックネームとアバター・ログインの情報・通知の宛先を消します。元に戻せません。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          消した情報は、障害に備えたデータベースの復元用の記録に最大 30
          日残り、その後に消えます。アクセスの記録は最大 7 日で消えます。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          通知の宛先は、アプリの「せってい」で通知をオフにしたとき、アカウントを削除したとき、Apple
          から使えなくなったと知らされたとき（アプリを削除したときなど）、同じ端末で別のアカウントを使い始めたときに消します。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          利用規約に反したために利用を止めたアカウントは、アプリから削除できません。削除をご希望の場合は、下の問い合わせ先へご連絡ください。
        </p>
      </DocumentSection>

      <DocumentSection title="開示・訂正・利用停止などのご請求">
        <p className={lead}>
          当方が保存している情報について、利用目的の通知、開示、訂正・追加・削除、利用の停止・消去、第三者への提供の停止、第三者への提供の記録の開示をご請求いただけます。
          下の問い合わせ先へメールでご連絡ください。手数料はいただきません。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          ご本人（またはその保護者の方）であることを確かめるため、アプリでの操作や、追加の情報をお願いすることがあります。
          ニックネームの変更とアカウントの削除は、アプリからいつでも行えます。
        </p>
      </DocumentSection>

      <DocumentSection title="子どもの利用について">
        <p className={lead}>
          本サービスは子どもが使うことを想定しています。AI
          鑑定やアバターの確認のために絵を送る前に、
          アプリの画面で送り先を説明し、同意を得ています。おうちの方といっしょに読んでいただくようお願いしています。
          保護者の方からのご相談やご請求も、下の問い合わせ先で受け付けます。
        </p>
      </DocumentSection>

      <DocumentSection title="このポリシーを変えるとき">
        <p className={lead}>
          内容を変えるときは、このページの最終更新日を改めます。大切な変更は、このサイトやアプリでお知らせします。
        </p>
      </DocumentSection>

      <DocumentSection title="問い合わせ">
        <p className={lead}>
          本ポリシーや情報の扱いについてのお問い合わせ・苦情は、
          <a href={`mailto:${SUPPORT_EMAIL}`} className={`${textLink} text-text`}>
            {SUPPORT_EMAIL}
          </a>
          へお送りください（
          <Link to="/support" className={`${textLink} text-text`}>
            おといあわせ
          </Link>
          ）。
        </p>
      </DocumentSection>
    </div>
  );
}
